import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Download, TrendingUp } from "lucide-react";

const PRESETS = [
  { k: "today", label: "TODAY" },
  { k: "yesterday", label: "YESTERDAY" },
  { k: "this_week", label: "THIS WEEK" },
  { k: "last_week", label: "LAST WEEK" },
  { k: "this_month", label: "THIS MONTH" },
  { k: "last_month", label: "LAST MONTH" },
  { k: "this_year", label: "THIS YEAR" },
  { k: "last_year", label: "LAST YEAR" },
  { k: "custom", label: "CUSTOM" },
];

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const intFmt = (n) => Number(n || 0).toLocaleString();

export default function AnalyticsPanel({ client }) {
  const [preset, setPreset] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { preset };
      if (preset === "custom") {
        if (customFrom) params.from_ts = new Date(customFrom).toISOString();
        if (customTo) params.to_ts = new Date(customTo).toISOString();
        delete params.preset;
      }
      const { data: d } = await client.get("/admin/analytics/summary", { params });
      setData(d);
      // Also load the raw orders in the same window so the search + detail
      // modal can drill down without a second click.
      const { data: hist } = await client.get("/admin/orders/history", {
        params: {
          from_ts: d.range.start,
          to_ts: d.range.end,
        },
      });
      setOrders(hist || []);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [client, preset, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      const params = { preset };
      if (preset === "custom") {
        if (customFrom) params.from_ts = new Date(customFrom).toISOString();
        if (customTo) params.to_ts = new Date(customTo).toISOString();
        delete params.preset;
      }
      const r = await client.get("/admin/analytics/export", {
        params, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-${preset}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Export failed");
    }
  };

  const filteredOrders = useMemo(() => {
    if (!search.trim()) return orders;
    const s = search.trim().toLowerCase();
    return orders.filter((o) => {
      return (
        (o.order_number || "").toLowerCase().includes(s) ||
        (o.customer_name || "").toLowerCase().includes(s) ||
        (o.customer_phone || "").toLowerCase().includes(s) ||
        (o.promo_code || "").toLowerCase().includes(s) ||
        (o.items || []).some((i) => (i.name || "").toLowerCase().includes(s))
      );
    });
  }, [orders, search]);

  return (
    <div className="space-y-4" data-testid="analytics-panel">
      {/* Filter bar */}
      <div className="border-2 border-slate-900 bg-white p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.k}
              data-testid={`analytics-preset-${p.k}`}
              onClick={() => setPreset(p.k)}
              className={`border-2 px-3 py-1.5 font-heading text-[10px] tracking-wider ${
                preset === p.k
                  ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex-1" />
          <button
            data-testid="analytics-export-btn"
            onClick={exportCsv}
            className="flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-1.5 font-heading text-xs tracking-wider text-slate-900 hover:bg-slate-100"
          >
            <Download className="h-3.5 w-3.5" /> EXPORT CSV
          </button>
        </div>
        {preset === "custom" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">From</span>
              <input
                data-testid="analytics-from"
                type="datetime-local"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">To</span>
              <input
                data-testid="analytics-to"
                type="datetime-local"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2"
              />
            </label>
          </div>
        )}
        {loading && (
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Loading…</div>
        )}
      </div>

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Kpi label="Orders" value={intFmt(data.totals.orders)} accent="#1d4ed8" testid="kpi-orders" />
            <Kpi label="Gross Item Sales" value={money(data.totals.gross_items)} accent="#0f766e" testid="kpi-gross" />
            <Kpi label="Discounts" value={money(data.totals.discounts)} accent="#dc2626" testid="kpi-discounts" />
            <Kpi label="Fees Collected" value={money(data.totals.fees)} accent="#7c3aed" testid="kpi-fees" />
            <Kpi label="Tips" value={money(data.totals.tips)} accent="#16a34a" testid="kpi-tips" />
            <Kpi label="Net Sales" value={money(data.totals.net)} accent="#111827" testid="kpi-net" />
            <Kpi label="Avg Order Value" value={money(data.totals.avg_order_value)} testid="kpi-aov" />
            <Kpi label="Avg Items / Order" value={Number(data.totals.avg_items_per_order || 0).toFixed(2)} testid="kpi-aip" />
          </div>

          {/* Two-column: Status breakdown + Financial breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Panel title="Order Status Breakdown" testid="status-breakdown">
              <StatusBars counts={data.status_counts} total={data.totals.orders} />
            </Panel>
            <Panel title="Financial Breakdown" testid="financial-breakdown">
              <FinRow label="Gross item sales" value={money(data.financial_breakdown.gross_item_sales)} />
              <FinRow label="Item discounts (deputy)" value={`− ${money(data.financial_breakdown.item_discounts)}`} negative />
              <FinRow label="Subtotal after discounts" value={money(data.financial_breakdown.subtotal_after_discounts)} />
              <FinRow label="Fees collected" value={money(data.financial_breakdown.fees)} />
              <FinRow label="Tips" value={money(data.financial_breakdown.tips)} />
              <FinRow label="Final charged (Stripe)" value={money(data.financial_breakdown.final_charged)} strong />
              {data.financial_breakdown.fee_discounts > 0 && (
                <div className="mt-2 border-t border-slate-100 pt-2 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Fees waived via promos: {money(data.financial_breakdown.fee_discounts)}
                </div>
              )}
            </Panel>
          </div>

          {/* Daily orders chart */}
          <Panel
            title="Orders Per Day"
            action={
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                <TrendingUp className="h-3 w-3" /> Trend
              </div>
            }
            testid="orders-trend-chart"
          >
            <DailyChart series={data.daily} />
          </Panel>

          {/* Payments + Promos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Panel title="Payments" testid="payments-panel">
              <FinRow label="Successful transactions" value={intFmt(data.payments.successful)} />
              <FinRow label="Total collected" value={money(data.payments.total_paid)} />
              <FinRow label="Avg transaction" value={money(data.payments.avg_transaction)} />
              <FinRow label="Refunded" value={`${data.payments.refunded} · ${money(data.payments.refunded_amount)}`} />
              <FinRow label="Method" value="Stripe (Card · Apple Pay · Google Pay)" />
            </Panel>
            <Panel title="Promo Codes" testid="promos-analytics-panel">
              {Object.keys(data.promos.counts).length === 0 && (
                <div className="text-xs text-slate-500 py-3">No promos redeemed in this range.</div>
              )}
              {Object.entries(data.promos.counts).map(([k, v]) => (
                <FinRow
                  key={k}
                  label={`${k[0].toUpperCase() + k.slice(1)} — ${v} redemption${v === 1 ? "" : "s"}`}
                  value={money(data.promos.amount_discounted[k] || 0)}
                />
              ))}
              <div className="mt-2 border-t border-slate-200 pt-2 text-xs text-slate-600 space-y-1">
                <div>Band codes total: <span className="tabular font-medium">{data.promos.band_total}</span></div>
                <div>Band codes used: <span className="tabular font-medium text-red-600">{data.promos.band_used}</span></div>
                <div>Band codes remaining: <span className="tabular font-medium text-emerald-700">{data.promos.band_unused}</span></div>
                <div>Redeemed in this range: <span className="tabular font-medium">{data.promos.band_used_in_range}</span></div>
              </div>
            </Panel>
          </div>

          {/* Item leaderboard */}
          <Panel title="Item Performance" testid="items-leaderboard">
            {data.items.length === 0 ? (
              <div className="text-xs text-slate-500 py-3">No items sold in this range.</div>
            ) : (
              <div className="overflow-auto">
                <table className="w-full text-xs">
                  <thead className="text-left text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="py-2 uppercase tracking-[0.15em]">Item</th>
                      <th className="py-2 text-right uppercase tracking-[0.15em]">Qty</th>
                      <th className="py-2 text-right uppercase tracking-[0.15em]">Revenue</th>
                      <th className="py-2 text-right uppercase tracking-[0.15em]">Orders</th>
                      <th className="py-2 text-right uppercase tracking-[0.15em]">Avg/Order</th>
                      <th className="py-2 text-right uppercase tracking-[0.15em]">% Sales</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.items.map((it) => (
                      <tr key={it.item_id}>
                        <td className="py-1.5 font-medium text-slate-900">{it.name}</td>
                        <td className="py-1.5 text-right tabular">{it.qty}</td>
                        <td className="py-1.5 text-right tabular text-[#0f766e] font-medium">{money(it.revenue)}</td>
                        <td className="py-1.5 text-right tabular">{it.orders_containing}</td>
                        <td className="py-1.5 text-right tabular">{it.avg_qty_per_order}</td>
                        <td className="py-1.5 text-right tabular">{it.pct_of_revenue}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          {/* Order search + list */}
          <Panel
            title={`Orders (${filteredOrders.length})`}
            action={
              <input
                data-testid="analytics-search"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search #, name, item, promo…"
                className="border border-slate-300 px-2 py-1 text-xs w-56"
              />
            }
            testid="analytics-orders"
          >
            <div className="overflow-auto max-h-[500px]">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white border-b border-slate-200 text-slate-500 text-left">
                  <tr>
                    <th className="py-2 uppercase tracking-[0.15em]">#</th>
                    <th className="py-2 uppercase tracking-[0.15em]">When</th>
                    <th className="py-2 uppercase tracking-[0.15em]">Customer</th>
                    <th className="py-2 uppercase tracking-[0.15em]">Items</th>
                    <th className="py-2 uppercase tracking-[0.15em]">Promo</th>
                    <th className="py-2 text-right uppercase tracking-[0.15em]">Total</th>
                    <th className="py-2 uppercase tracking-[0.15em]">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-6 text-slate-400 uppercase tracking-[0.2em]">
                        No orders
                      </td>
                    </tr>
                  )}
                  {filteredOrders.map((o) => (
                    <tr key={o.id} data-testid={`analytics-order-${o.id}`}>
                      <td className="py-1.5 font-heading tabular text-[#1d4ed8]">{o.order_number}</td>
                      <td className="py-1.5 text-slate-500 tabular">
                        {new Date(o.created_at).toLocaleString([], {
                          month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit",
                        })}
                      </td>
                      <td className="py-1.5">{o.customer_name}</td>
                      <td className="py-1.5 text-slate-600">
                        {o.items.reduce((a, i) => a + i.quantity, 0)}
                      </td>
                      <td className="py-1.5 text-[11px]">
                        {o.promo_type ? (
                          <span className="border border-slate-300 px-1.5 py-0.5 font-heading text-[9px] tracking-[0.15em] text-[#7c3aed]">
                            {o.promo_type.toUpperCase()}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="py-1.5 text-right tabular font-medium">{money(o.total)}</td>
                      <td className="py-1.5 text-[11px] uppercase tracking-[0.15em] text-slate-500">{o.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="border-2 border-slate-900 bg-white p-3"
    >
      <div className="text-[9px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
      <div
        className="font-heading text-2xl tabular mt-0.5"
        style={{ color: accent || "#0f172a" }}
      >
        {value}
      </div>
    </div>
  );
}

function Panel({ title, children, action, testid }) {
  return (
    <div className="border-2 border-slate-900 bg-white" data-testid={testid}>
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">{title}</div>
        {action}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function FinRow({ label, value, negative, strong }) {
  return (
    <div className={`flex items-center justify-between py-1 text-sm ${strong ? "border-t border-slate-300 mt-1 pt-2 font-heading tracking-wider text-slate-900" : ""}`}>
      <span className="text-slate-600">{label}</span>
      <span className={`tabular ${negative ? "text-red-600" : "text-slate-900"} ${strong ? "text-lg" : ""}`}>{value}</span>
    </div>
  );
}

function StatusBars({ counts, total }) {
  const colors = {
    pending: "#ca8a04",
    preparing: "#1d4ed8",
    ready: "#16a34a",
    completed: "#0f172a",
    canceled: "#94a3b8",
    refunded: "#dc2626",
  };
  const entries = Object.entries(counts);
  return (
    <div className="space-y-2">
      {entries.map(([k, v]) => {
        const pct = total > 0 ? Math.round((v / total) * 100) : 0;
        return (
          <div key={k}>
            <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.15em] text-slate-500">
              <span>{k}</span>
              <span className="tabular text-slate-900">
                {v} · {pct}%
              </span>
            </div>
            <div className="mt-1 h-2 bg-slate-100">
              <div
                className="h-full"
                style={{ width: `${pct}%`, background: colors[k] || "#94a3b8" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DailyChart({ series }) {
  if (!series || series.length === 0) {
    return <div className="text-xs text-slate-500 py-3">No data.</div>;
  }
  const w = 640, h = 160, pad = 24;
  const max = Math.max(1, ...series.map((s) => s.orders));
  const barW = Math.max(6, (w - pad * 2) / series.length - 4);
  return (
    <div className="overflow-x-auto">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-40">
        {series.map((s, i) => {
          const x = pad + i * (barW + 4);
          const barH = Math.round(((h - pad * 2) * s.orders) / max);
          const y = h - pad - barH;
          return (
            <g key={s.date}>
              <rect
                x={x} y={y} width={barW} height={barH}
                fill="#1d4ed8"
                data-testid={`daily-bar-${s.date}`}
              >
                <title>{s.date} — {s.orders} orders · ${s.net.toFixed(2)}</title>
              </rect>
              {series.length <= 30 && (
                <text
                  x={x + barW / 2} y={h - 6}
                  fontSize="8" textAnchor="middle" fill="#94a3b8"
                >
                  {s.date.slice(5)}
                </text>
              )}
              <text
                x={x + barW / 2} y={y - 3}
                fontSize="9" textAnchor="middle" fill="#0f172a"
              >
                {s.orders}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
